import { SupplyType } from "../utils/booking/supplyType";
import axios from "axios";
import toast from "react-hot-toast";
import { API_HEADER } from "../constant/common";
import { ENDPOINT } from "../constant/environment";
import { toastMessage } from "../helpers/toast/toastMessage";
import { decryptData, encryptData, getHeaderConfig } from "../utils/api/encryption";
import { Messages } from "../constant/messages/toastMessages";
import { consoleLog } from "../utils/consoleLog/consoleLog";
import { hasUsableJWT } from "../utils/authentication/hasUsableJWT";
import { recordCurrencyFromHotelAvailability, recordCurrencyFromRoomPrices } from "../utils/searchCurrency";
import { isWidgetRuntime } from "../utils/widgetRuntime";
import { MCP_WIDGET_TOOLS } from "../constant/mcpWidget";
import { callWidgetTool, withoutUserId } from "../widgets/mcp/widgetTools";

export const getHotelDetailsAPI = async (
  accessJWT: string,
  userId: string,
  hotelId: string,
  controller: AbortController,
  isLoading?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (!hasUsableJWT(accessJWT, "getHotelDetailsAPI")) return null;
  const URL = `${ENDPOINT}/app/v1/hotel/hotelId`;
  const body = {
    userId: userId,
    hotelId: hotelId,
  };
  consoleLog(["API REQUEST", URL, body]);
  const config = getHeaderConfig(controller.signal, accessJWT);
  try {
    if (isLoading) isLoading(true);
    const response = await axios.post(URL, encryptData(body), config);
    if (isLoading) isLoading(false);
    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog(["API RESPONSE", URL, parseData]);
      if (parseData?.data) return parseData?.data;
      return null;
    } else if (response?.status === 404) {
      toastMessage.error(Messages["hotel.search.failure.404"].message);
      return null;
    } else {
      return null;
    }
  } catch (error) {
    if (isLoading) isLoading(false);
    return null;
  }
};

export interface iHotelDetailsByUnicaIdArgs {
  unicaId: string;
  supplier: string;
  hotelId: string;
}

export const getHotelDetailsByUnicaId = async (
  accessJWT: string,
  args: iHotelDetailsByUnicaIdArgs,
  controller: AbortController,
  isLoading?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (!hasUsableJWT(accessJWT, "getHotelDetailsByUnicaId")) return null;
  const URL = `${ENDPOINT}/app/v1/search/hotels/details-by-unicaid`;
  const config = getHeaderConfig(controller.signal, accessJWT);
  const body = { ...args };
  try {
    consoleLog(["API REQUEST", URL, body]);
    if (isLoading) isLoading(true);
    const response = await axios.post(URL, encryptData(body), config);
    if (isLoading) isLoading(false);
    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog(["API RESPONSE", URL, parseData]);
      if (parseData?.data) return parseData?.data;
      return null;
    }
    return null;
  } catch (error) {
    if (isLoading) isLoading(false);
    return null;
  }
};

const readRoomDetails = (parseData: any) => {
  if (parseData?.data) {
    const inner = parseData.data;
    recordCurrencyFromRoomPrices(inner);
    const hasRoomsAtInner = Array.isArray(inner?.rooms);
    const hasRoomsAtNested =
      inner?.data && typeof inner.data === "object" && Array.isArray(inner.data.rooms);
    if (!hasRoomsAtInner && hasRoomsAtNested) {
      const unwrappedSessionId = inner.data?.sessionId || inner?.sessionId;
      return {
        ...inner.data,
        ...(inner?.searchResultUniqueId
          ? { searchResultUniqueId: inner.searchResultUniqueId }
          : {}),
        ...(unwrappedSessionId ? { sessionId: unwrappedSessionId } : {}),
      };
    }
    return inner;
  }
  return parseData?.statusCode;
};

export const getRoomDetailsAPI = async (
  accessJWT: string,
  roomDetailsBody: any,
  controller: AbortController,
  isLoading?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const URL = `${ENDPOINT}/app/v1/hotel/${roomDetailsBody.hotelId}`;
  const config = getHeaderConfig(controller.signal, accessJWT);
  const body = {
    version: "v2",
    ...roomDetailsBody,
    pagination: {
      count: 100,
      offset: 0,
    },
  };
  if (isWidgetRuntime()) {
    if (isLoading) isLoading(true);
    const response = await callWidgetTool(MCP_WIDGET_TOOLS.rooms, withoutUserId(body), controller.signal);
    if (isLoading) isLoading(false);
    return response ? readRoomDetails(response) : null;
  }
  try {
    consoleLog(["API REQUEST", URL, body]);

    if (isLoading) isLoading(true);
    const response = await axios.post(URL, encryptData(body), config);
    if (isLoading) isLoading(false);
    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog(["API RESPONSE", URL, parseData]);
      return readRoomDetails(parseData);
    } else return null;
  } catch (error) {
    if (isLoading) isLoading(false);
    return null;
  }
};

export interface iHotelsArgs {
  uniqueId: string;
  sessionId: string;
  // Rate-level supplier from the picked roomPrices[j].supplier. BE requires
  // this as a top-level field alongside uniqueId/sessionId (in addition to
  // the __SUPPLIER_SPLIT__ suffix already embedded inside uniqueId, kept
  // for backward-compat).
  supplier?: string;
  // Rate-level hotelId from the picked roomPrices[j].hotelId — can differ
  // from the parent hotel's hotelId in multi-supplier scenarios.
  hotelId?: string;
  // Per-hotel supplier id (`hotels[N].unicaId`, e.g. "zenvoya#<hotelId>").
  // Mirrors the `unicaId` sent to the room-pricing API (`getRoomDetailsAPI`)
  // so BE can resolve the same hotel row it priced.
  unicaId?: string;
}

export interface iHoteRoomAvailableArgs {
  userId: string;
  hotels: iHotelsArgs[];
  supplyType?: SupplyType;
}

export const checkHotelRoomAvailable = async (
  accessJWT: string,
  args: iHoteRoomAvailableArgs,
  controller: AbortController,
  isLoading?: any
): Promise<any> => {
  const URL = `${ENDPOINT}/app/v1/hotel/availability`;
  const config = getHeaderConfig(controller.signal, accessJWT);
  const body = {
    version: "v2",
    ...args,
  };
  try {
    if (isLoading) isLoading(true);
    consoleLog(["API REQUEST", URL, body]);

    const response = await axios.post(URL, encryptData(body), config);

    if (isLoading) isLoading(false);

    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog(["API RESPONSE", URL, parseData]);
      // V2 chat-hotel diagnostic: this fn silently returns null whenever
      // parseData.data is missing — hiding the actual statusCode / message.
      // Surface the raw envelope so we can see WHY the BE rejected (session
      // expired? unknown sessionId? supplier-suffix issue?).
      // eslint-disable-next-line no-console
      console.log("[v2-FE-hotel-availability-raw]", {
        httpStatus: response?.status,
        statusCode: parseData?.statusCode,
        message: parseData?.message,
        rawError: parseData?.rawError,
        hasInnerData: !!parseData?.data,
        parseData,
      });
      if (parseData?.data) {
        const inner = parseData.data;
        // Availability arrives in two shapes; downstream callers
        // (useSelectRoomHooks.ts:182, useHotelCardLogic.tsx:359,
        // TravelersHotelContext.tsx:225, useTravelerInformation.ts:142)
        // all expect `response.data` to be the entries array:
        //   1. Flat:    inner = [ { supplier, uniqueId, sessionId, prebookId, pricing, ttl }, ... ]
        //   2. Wrapped: inner = { status: "success", statusCode: 200,
        //               data: [...entries], hotel_timing: {...} }
        // Normalize to `{ data: [...entries] }` so callers don't need to
        // sniff which shape they got.
        recordCurrencyFromHotelAvailability(inner);
        if (Array.isArray(inner)) {
          return { data: inner };
        }
        return inner;
      }
      return null;
    } else return null;
  } catch (error: any) {
    // eslint-disable-next-line no-console
    console.warn("[v2-FE-hotel-availability-raw] threw", {
      httpStatus: error?.response?.status,
      data: error?.response?.data,
      message: error?.message,
    });
    if (isLoading) isLoading(false);
    return null;
  }
};

export interface iBookHotelRoomArgs {
  clientId: string;
  userId: number;
  passengerDetails: PassengerDetail[];
  hotels: iHotelRoomBookArgs[];
}

export interface iHotelRoomBookArgs {
  prebookId: string;
  sessionId: string;
  uniqueId?: string;
  // Echoed from the /hotel/availability request (BE requires the same three
  // identifiers on the subsequent /itinerary/prebooking payload). Sourced from
  // selectedHotelRoom.supplier / rateHotelId / unicaId (already stamped by
  // updateSelectedHotelRoomData in useSelectRoomHooks).
  supplier?: string;
  hotelId?: string;
  unicaId?: string;
  supplyType?: SupplyType;
}

export interface PassengerDetail {
  passengerId: number;
  room_no: number;
  title: string;
  paxType: string;
  leadPax: boolean;
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  phoneNumber: string;
  countryName: string;
  iso: string;
}

export const bookHotelRoomAPI = async (args: iBookHotelRoomArgs, isLoading?: any): Promise<any> => {
  const URL = `${ENDPOINT}/app/v1/book/hotel`;
  const config = {
    headers: API_HEADER,
  };
  const body = {
    ...args,
  };
  try {
    if (isLoading) isLoading(true);
    const response = await axios.post(URL, body, config);
    if (isLoading) isLoading(false);
    toast.dismiss();
    if (response?.data?.statusCode !== 200) throw new Error(response?.data.message);

    return response?.data?.data;
  } catch (error: any) {
    toast.dismiss();
    if (isLoading) isLoading(false);
    toastMessage.error(error.message);
    return null;
  }
};
